/**
 * Client-side mirror of the server's delivery rules, so the Delivery tab can show errors and a price
 * preview before anything is saved. The server stays the authority and repeats every check:
 *  - ring validation: common/ranges/ContiguousRanges.kt + StoreDeliverySettingsService.validBands
 *  - pricing:         provider/store/delivery/DeliveryPricingService.kt
 *
 * Amounts are handled as integer hundredths (cents / hundredths of a km) so 0.1 + 0.2 style float
 * drift can never make a boundary distance fall into the wrong ring.
 */

// Integer part optional (a number input can hold ".5"), but something must be there.
const TWO_DECIMALS = /^(\d+(\.\d{1,2})?|\.\d{1,2})$/;
// The server's NUMERIC bounds (StoreDeliverySettingsService MAX_MONEY / MAX_KM).
export const MAX_MONEY = 1000000;
export const MAX_KM = 10000;

const isBlank = (v) => v === undefined || v === null || String(v).trim() === '';

/** Hundredths of a unit for a valid non-negative <= 2 decimal string; null when it is not one. */
export const toHundredths = (value) => {
  if (isBlank(value)) return null;
  const s = String(value).trim();
  if (!TWO_DECIMALS.test(s)) return null;
  return Math.round(Number(s) * 100);
};

/**
 * Why a single numeric field is unusable, or null when it is fine/empty.
 * 'negative' | 'decimals' | 'invalid'
 */
export const numberProblem = (value) => {
  if (isBlank(value)) return null;
  const s = String(value).trim();
  const n = Number(s);
  if (!Number.isFinite(n)) return 'invalid';
  if (n < 0) return 'negative';
  if (!TWO_DECIMALS.test(s)) return 'decimals';
  return null;
};

/**
 * Validates the ring table the way the server does. `rings` is the editor state: strings
 * `{ fromKm, toKm, surcharge }`, an empty `toKm` meaning "no upper limit".
 *
 * @returns {Array<{row: number, field: 'fromKm'|'toKm'|'surcharge'|null, code: string, required?: boolean, params?: object}>}
 *   `row` indexes the INPUT array (-1 = the table as a whole). `required` marks "a value is still
 *   missing" errors, which the UI only shows once the owner tries to save.
 */
export const validateRings = (rings) => {
  const errors = [];
  if (!rings || rings.length === 0) {
    return [{ row: -1, field: null, code: 'none', required: true }];
  }

  const valid = []; // rows whose numbers are usable, for the cross-row checks
  rings.forEach((ring, row) => {
    let usable = true;

    if (isBlank(ring.fromKm)) {
      errors.push({ row, field: 'fromKm', code: 'fromRequired', required: true });
      usable = false;
    } else {
      const problem = numberProblem(ring.fromKm);
      if (problem) {
        errors.push({ row, field: 'fromKm', code: problem });
        usable = false;
      } else if (Number(ring.fromKm) > MAX_KM) {
        errors.push({ row, field: 'fromKm', code: 'tooBig', params: { max: MAX_KM } });
        usable = false;
      }
    }

    if (!isBlank(ring.toKm)) {
      const problem = numberProblem(ring.toKm);
      if (problem) {
        errors.push({ row, field: 'toKm', code: problem });
        usable = false;
      } else if (Number(ring.toKm) > MAX_KM) {
        errors.push({ row, field: 'toKm', code: 'tooBig', params: { max: MAX_KM } });
        usable = false;
      }
    }

    if (isBlank(ring.surcharge)) {
      errors.push({ row, field: 'surcharge', code: 'surchargeRequired', required: true });
    } else {
      const problem = numberProblem(ring.surcharge);
      if (problem) errors.push({ row, field: 'surcharge', code: problem });
      else if (Number(ring.surcharge) > MAX_MONEY) {
        errors.push({ row, field: 'surcharge', code: 'tooBig', params: { max: MAX_MONEY } });
      }
    }

    if (usable) {
      const from = toHundredths(ring.fromKm);
      const to = isBlank(ring.toKm) ? null : toHundredths(ring.toKm);
      if (to !== null && to <= from) {
        errors.push({ row, field: 'toKm', code: 'endAfterStart' });
      } else {
        valid.push({ row, from, to });
      }
    }
  });

  if (valid.length === 0) return errors;

  const sorted = [...valid].sort((a, b) => a.from - b.from || a.row - b.row);

  if (sorted[0].from !== 0) {
    errors.push({ row: sorted[0].row, field: 'fromKm', code: 'firstNotZero' });
  }
  for (let i = 0; i < sorted.length - 1; i += 1) {
    const prev = sorted[i];
    const next = sorted[i + 1];
    if (prev.to === null) {
      errors.push({ row: prev.row, field: 'toKm', code: 'openNotLast' });
    } else if (next.from < prev.to) {
      errors.push({ row: next.row, field: 'fromKm', code: 'overlap', params: { end: prev.to / 100 } });
    } else if (next.from > prev.to) {
      errors.push({ row: next.row, field: 'fromKm', code: 'gap', params: { end: prev.to / 100 } });
    }
  }
  return errors;
};

/**
 * What a buyer `distanceKm` away pays, by the server's rules: the ring owning [from, to) adds its
 * surcharge to the base fee. Beyond the last closed ring the store does not deliver. When a
 * `basketSubtotal` is given and the store has a valid `freeThreshold`, the whole fee is waived once
 * the items total reaches the threshold (DeliveryPricingService: `itemsSubtotal >= threshold`); the
 * ring check still applies first, so an out-of-range buyer cannot order even with a big basket.
 *
 * `basketSubtotal` and `freeThreshold` are optional: blank means "not entered" / "no threshold".
 *
 * @returns {{status: 'ok', base: number, surcharge: number, total: number, waived: boolean,
 *   freeThreshold: number|null} | {status: 'outOfRange'} | {status: 'invalid'}
 *   | {status: 'badDistance'} | {status: 'badBaseFee'} | {status: 'badBasket'}}
 *   `invalid` = the ring table is broken. `freeThreshold` is the usable threshold (or null).
 */
export const previewDistanceFee = ({
  baseFee, rings, distanceKm, basketSubtotal = '', freeThreshold = '',
}) => {
  const dist = toHundredths(distanceKm);
  const base = toHundredths(baseFee);
  if (dist === null) return { status: 'badDistance' };
  if (base === null) return { status: 'badBaseFee' };
  if (validateRings(rings).length > 0) return { status: 'invalid' };

  let basket = null;
  if (!isBlank(basketSubtotal)) {
    basket = toHundredths(basketSubtotal);
    if (basket === null) return { status: 'badBasket' };
  }
  const thresholdCents = toHundredths(freeThreshold);
  const threshold = thresholdCents !== null && thresholdCents > 0 ? thresholdCents : null;

  const ring = rings.find((r) => {
    const from = toHundredths(r.fromKm);
    const to = isBlank(r.toKm) ? null : toHundredths(r.toKm);
    return dist >= from && (to === null || dist < to);
  });
  if (!ring) return { status: 'outOfRange' };
  const surcharge = toHundredths(ring.surcharge);
  const waived = threshold !== null && basket !== null && basket >= threshold;
  return {
    status: 'ok',
    base: base / 100,
    surcharge: surcharge / 100,
    total: waived ? 0 : (base + surcharge) / 100,
    waived,
    freeThreshold: threshold === null ? null : threshold / 100,
  };
};
